import React, { PureComponent } from "react";
import {
  Card,
  CardBody,
  Col,
  Button,
  ButtonToolbar,
  Table,
  Modal,
} from "reactstrap";
import { reduxForm } from "redux-form";
import { withTranslation } from "react-i18next";
import PropTypes from "prop-types";
import XLSX from "xlsx";
import LoadingOverlay from 'react-loading-overlay';
import Util from '../../../Common/util';
import API from '../../../Common/api';
import { addAnalytics } from '../../../Common/analytics';
import exportToCSVNew from '../../../Common/exportcsvnew';
import "./styles.css";

// Constants
const ALLOWED_FILE_EXTENSIONS = ['xlsx', 'csv', 'xls'];
const API_ENDPOINT = "/jogeshwari/upload/v1/";
const MASTER_TYPE = "sku7_shelflife_master";
const TEMPLATE_FILENAME = "SKU7_ShelfLifeMaster_Template";
const REQUIRED_HEADERS = { "Brand": "", "Product Code": "", "MRP": "", "Shelf Life": "" };

const MODAL_TYPES = {
  RESPONSE: 'response',
  SUCCESS: 'success',
  FAILED: 'failed'
};

const FILE_INPUT_STYLES = {
  VALID: { color: "", width: "85%" },
  INVALID: { color: "transparent", width: "26%" },
  RESET: { color: "transparent", width: "26%" }
};

class SKU7ShelfLifeUploadForm extends PureComponent {
  static propTypes = {
    t: PropTypes.func.isRequired,
    reset: PropTypes.func.isRequired,
  };

  constructor(props) {
    super(props);
    this.handleFileInputChange = this.handleFileInputChange.bind(this);
    this.handleFileSubmit = this.handleFileSubmit.bind(this);
    this.toggleErrorModal = this.toggleErrorModal.bind(this);
    this.closeErrorModal = this.closeErrorModal.bind(this);
    this.toggleSuccessModal = this.toggleSuccessModal.bind(this);
    this.closeSuccessModal = this.closeSuccessModal.bind(this);

    this.fileInputRef = React.createRef();
    this.uploadLabelRef = React.createRef();

    this.state = {
      isLoading: false,
      isLoadingOverlay: false,
      file: {},
      uploadErrors: [],
      showUploadErrorsTable: false,
      isFileInputDisabled: false,
      errorModalOpen: false,
      errorModalMessage: "",
      successModalOpen: false,
      successMessage: "",
      userName: "",
      selectedLocation: ""
    };
  }

  componentDidMount() {
    this.initializeUserData();
  }

  initializeUserData = () => {
    const cookieUsername = Util.getCookie("token");
    const location = Util.getCookie("selectedLocation");
    const username = Util.accessCookie("token");
    
    this.setState({
      userName: cookieUsername,
      selectedLocation: location,
    });

    localStorage.setItem("userName", username);
  }

  toggleResponseModal = () => {
    this.setState({
      errorModalOpen: !this.state.errorModalOpen
    });
  }

  closeSuccessModal = () => {
    this.setState({ successModalOpen: false });
  }

  toggleSuccessModal = () => {
    this.setState((prevState) => ({ successModalOpen: !prevState.successModalOpen }));
  }

  toggleErrorModal = () => {
    this.setState((prevState) => ({ errorModalOpen: !prevState.errorModalOpen }));
  }

  closeErrorModal = () => {
    this.setState({ errorModalOpen: false });
  }

  handleFileInputChange = (evt) => {
    const files = evt.target.files;
    const filePath = evt.target.value;
    
    if (!files || !files[0]) {
      this.setState({
        isFileInputDisabled: false,
        file: {},
      });
      return;
    }

    const fileExtension = this.getFileExtension(filePath);
    const isValidFormat = ALLOWED_FILE_EXTENSIONS.includes(fileExtension);

    this.setState({
      file: files[0],
      isFileInputDisabled: false,
    });

    this.updateFileInputUI(isValidFormat);
  }

  getFileExtension = (filePath) => {
    const fileExtensionParts = filePath.split(".");
    return fileExtensionParts[fileExtensionParts.length - 1].toLowerCase();
  }

  updateFileInputUI = (isValidFormat) => {
    const styles = isValidFormat ? FILE_INPUT_STYLES.VALID : FILE_INPUT_STYLES.INVALID;
    
    if (this.fileInputRef.current) {
      this.fileInputRef.current.style.color = styles.color;
      this.fileInputRef.current.style.width = styles.width;
    }

    if (this.uploadLabelRef.current) {
      this.uploadLabelRef.current.style.display = isValidFormat ? "none" : "inline-block";
    }
  }

  handleFileSubmit = () => {
    const { file } = this.state;

    if (!file.name) {
      this.setState({
        errorModalOpen: true,
        errorModalMessage: "Select at least one File"
      });
      return;
    }

    this.setState({
      isLoading: true,
      isLoadingOverlay: true,
    });

    this.readAndProcessFile(file);
  }

  readAndProcessFile = (file) => {
    const reader = new FileReader();
    const supportsAsBinaryString = !!reader.readAsBinaryString;

    reader.onload = (e) => {
      this.processExcelFile(e.target.result, supportsAsBinaryString);
    };

    if (supportsAsBinaryString) {
      reader.readAsBinaryString(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  }

  processExcelFile = (binaryString, supportsAsBinaryString) => {
    try {
      const workbook = XLSX.read(binaryString, {
        type: supportsAsBinaryString ? "binary" : "array",
        bookVBA: true,
      });

      const worksheetName = workbook.SheetNames[0];
      const worksheet = workbook.Sheets[worksheetName];
      
      const data = XLSX.utils.sheet_to_json(worksheet, {
        raw: false,
        dateNf: "YYYY-MM-DD"
      });

      const rowArray = XLSX.utils.sheet_to_row_object_array(
        workbook.Sheets[worksheetName],
        { header: 1, blankRows: false }
      );

      const uploadedFileHeaders = rowArray[0];
      const sampleFileHeaders = Object.keys(REQUIRED_HEADERS);

      if (uploadedFileHeaders.length !== sampleFileHeaders.length) {
        this.setState({
          isLoading: false,
          errorModalOpen: true,
          errorModalMessage: "Invalid Format",
          isLoadingOverlay: false,
        });
        return;
      }

      this.transformAndUploadData(data);
    } catch (error) {
      this.handleFileProcessingError("Failed to process file");
    }
  }

  transformAndUploadData = (data) => {
    const transformedData = data.map(item => ({
      brand: item["Brand"] || "",
      sku7_code: item["Product Code"] || "",
      mrp: item["MRP"] || "",
      shelf_life: item["Shelf Life"] || ""
    }));

    const payload = {
      master_data: transformedData,
      master_type: MASTER_TYPE,
    };

    addAnalytics('Upload File', 'Upload SKU7 Shelf life', data);
    this.uploadToServer(payload);
  }

  uploadToServer = (payload) => {
    API.post(API_ENDPOINT, { param: payload })
      .then((response) => this.handleUploadSuccess(response))
      .catch((error) => this.handleUploadError(error));
  }

  handleUploadSuccess = (response) => {
    this.setState({
      showUploadErrorsTable: true,
      uploadErrors: response.data.data || [],
      isLoading: false,
      isLoadingOverlay: false,
    });

    if (response.data.success) {
      this.toggleSuccessModal();
      this.setState({
        successMessage: response.data.message
      });
    } else {
      this.toggleErrorModal();
    }

    this.resetFileInputUI();
  }

  handleUploadError = (error) => {
    this.setState({
      isLoadingOverlay: false,
      isLoading: false,
    });

    const errorMessage = (error && error.response && error.response.data && error.response.data.reason) 
      || "An unexpected error occurred. Please try again.";
    
    this.setState({
      errorModalOpen: true,
      errorModalMessage: errorMessage,
    });
  }

  handleFileProcessingError = (message) => {
    this.setState({
      isLoading: false,
      isLoadingOverlay: false,
      errorModalOpen: true,
      errorModalMessage: message,
    });
  }

  resetFileInputUI = () => {
    if (this.fileInputRef.current) {
      this.fileInputRef.current.style.color = "transparent";
      this.fileInputRef.current.style.width = "26%";
    }

    if (this.uploadLabelRef.current) {
      this.uploadLabelRef.current.style.display = "inline-block";
      this.uploadLabelRef.current.innerHTML = "Select and Upload SKU7 Shelf Life Master";
    }
  }

  downloadSampleTemplate = () => {
    exportToCSVNew([REQUIRED_HEADERS], TEMPLATE_FILENAME);
  }

  renderUploadErrorsTable = () => {
    const { showUploadErrorsTable, uploadErrors } = this.state;

    if (!showUploadErrorsTable || uploadErrors.length === 0) {
      return null;
    }

    return (
      <CardBody style={{ marginTop: 20 }}>
        <Table responsive className="table--bordered">
          <thead>
            <tr>
              <th>SKU</th>
              <th>Response</th>
            </tr>
          </thead>
          <tbody>
            {uploadErrors.map((item, index) => (
              <tr key={`${item.sku7_code}-${index}`}>
                <td>{item.sku7_code}</td>
                <td style={{ color: item.success ? "green" : "red" }}>
                  {item.message}
                </td>
              </tr>
            ))}
          </tbody>
        </Table>
      </CardBody>
    );
  }

  render() {
    const { 
      isLoadingOverlay, 
      isLoading, 
      isFileInputDisabled, 
      errorModalOpen, 
      errorModalMessage,
      successModalOpen,
      successMessage
    } = this.state;

    return (
      <Col xl={12}>
        <div className="page-title-section" style={{ marginBottom: "20px" }}>
          <h3 className="page-title">Products Shelf Life - Upload</h3>
        </div>
        {isLoadingOverlay && <LoadingOverlay active={true} spinner text='' />}
        <Card>
          <CardBody style={{ height: 220 }}>
            <form className="form form--inline" onSubmit={e => e.preventDefault()}>
              <div className="form__form-group col-xl-5" style={{ marginBottom: "0px" }}>
                <div className="row">
                  <div className="col-xl-12" style={{ border: "1px solid #f2f4f7" }}>
                    <input
                      style={{
                        paddingTop: 8,
                        height: 38,
                        color: "transparent",
                        width: "26%",
                        border: "0px",
                      }}
                      type="file"
                      id="shelfLifeFileInput"
                      onChange={this.handleFileInputChange}
                      ref={this.fileInputRef}
                    />
                    <label 
                      htmlFor="shelfLifeFileInput" 
                      id="shelfLifeUploadLabel" 
                      ref={this.uploadLabelRef}
                    >
                      Select and Upload SKU7 Shelf Life Master
                    </label>
                  </div>
                </div>
              </div>

              <div className="form__form-group col-xl-2">
                <ButtonToolbar className="form__button-toolbar">
                  <button
                    className="btn-sm button_enable"
                    style={{
                      fontWeight: "normal",
                      textTransform: "capitalize",
                      borderRadius: 5,
                      backgroundColor: "#70bbfd",
                      color: "#fff",
                      padding: "5px 25px",
                      fontSize: "14px",
                    }}
                    onClick={this.handleFileSubmit}
                    disabled={isFileInputDisabled}
                  >
                    {isLoading && (
                      <i
                        className="fa fa-refresh fa-spin"
                        style={{ marginRight: "5px" }}
                      />
                    )}
                    Submit
                  </button>
                </ButtonToolbar>
              </div>
            </form>

            <div style={{ paddingLeft: "17px" }}>
              <button
                className="download_sample_styling button_enable"
                style={{
                  backgroundColor: "#fff",
                  marginTop: 4,
                  marginBottom: 7,
                  fontWeight: "500",
                  textDecoration: "underline",
                  textTransform: "capitalize",
                }}
                onClick={this.downloadSampleTemplate}
              >
                Sample Template
              </button>
            </div>
          </CardBody>

          {this.renderUploadErrorsTable()}
        </Card>

        <Modal isOpen={errorModalOpen} toggle={this.toggleErrorModal} style={{ maxWidth: 578 }}>
          <div className="modal__header">
            <button
              className="lnr lnr-cross modal__close-btn"
              type="button"
              style={{ right: 15 }}
              onClick={this.toggleErrorModal}
            />
          </div>
          <div className="modal__body" style={{ paddingTop: 55, paddingBottom: 20 }}>
            <span
              className="lnr lnr-cross-circle modal__title-icon"
              style={{ color: "red" }}
            />
            <h4>{errorModalMessage || "This file contains some errors."}</h4>
          </div>
          <ButtonToolbar className="modal__footer">
            <Button
              style={{ backgroundColor: "#ddd", color: "#fff" }}
              className="modal_ok"
              onClick={this.closeErrorModal}
            >
              Close
            </Button>
          </ButtonToolbar>
        </Modal>

        <Modal isOpen={successModalOpen} toggle={this.toggleSuccessModal}>
          <div className="modal__header">
            <button
              className="lnr lnr-cross modal__close-btn"
              type="button"
              style={{ right: 15 }}
              onClick={this.toggleSuccessModal}
            />
          </div>
          <div className="modal__body" style={{ paddingTop: 55, paddingBottom: 20 }}>
            <span
              className="lnr lnr-thumbs-up modal__title-icon"
              style={{ color: "#4ce1b6" }}
            />
            <h4>{successMessage || "Successfully Updated"}</h4>
          </div>
          <ButtonToolbar className="modal__footer">
            <Button
              style={{ backgroundColor: "#24d6a3", color: "#fff" }}
              className="modal_ok"
              onClick={this.closeSuccessModal}
            >
              Ok
            </Button>
          </ButtonToolbar>
        </Modal>
      </Col>
    );
  }
}

export default reduxForm({
  form: "SKU7ShelfLifeUploadMaster",
})(withTranslation("common")(SKU7ShelfLifeUploadForm));